import React, { useContext, useEffect, useState } from 'react'
import Context from '../../context'
import getConfig from 'next/config'
import Head from 'next/head'
import { distance as getDistance } from '../../utils/distance'
import dayjs from 'dayjs'
import { useChangeCartStore } from '../../hooks/cart.hooks'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import SVG from '../../components/SVG'
import Image from 'next/image'
import { setPageViewData } from '../../utils/analytics'
dayjs.extend(customParseFormat)

const { publicRuntimeConfig } = getConfig()

const Store = ({ storeDetails }) => {
  const currentDay = dayjs().format('dddd')
  const { state } = useContext(Context)
  const [distance, setDistance] = useState<number>()
  const changeCartCall = useChangeCartStore()
  const isSelectable = !storeDetails.is_closed && !storeDetails.store_preview
  const isPreview = storeDetails.store_preview
  const isClosed = storeDetails.is_closed
  const mapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${storeDetails.latitude},${storeDetails.longitude}&zoom=16&size=1200x800&scale=2&markers=color:red&markers=size:mid|${storeDetails.latitude},${storeDetails.longitude}&key=${publicRuntimeConfig.MAPS_API_KEY}`
  const address = `pOpshelf, ${storeDetails.address}, ${storeDetails.city}, ${storeDetails.state} ${storeDetails.postalcode}`

  useEffect(() => {
    const origin = Object.keys(state.location).length ? { lat: state.location.lat, lng: state.location.lng } : { lat: state.cart.store?.latitude, lng: state.cart.store?.longitude }
    setDistance(getDistance(origin.lat, origin.lng, storeDetails.latitude, storeDetails.longitude, 'M'))
    const path = `${storeDetails.state}/${storeDetails.city}/${storeDetails.storenumber}`
    setPageViewData(`store-directory/${path}`, 'Store', path)
  }, [])

  useEffect(() => {
    const scheduleTableDays = document.querySelectorAll('.schedule__table-days p')
    const scheduleTableTime = document.querySelectorAll('.schedule__table-time p')
    scheduleTableDays.forEach((day, index) => {
      if (day.innerHTML === currentDay) {
        day.classList.add('bold-style')
        scheduleTableTime[index].classList.add('bold-style')
      }
    })
  }, [])

  const handleGetDirections = () => {
    const encodedAddress = encodeURIComponent(address)
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
    window.open(mapsUrl, '_blank')
  }

  const setStore = (store) => {
    changeCartCall(store.storenumber)
  }

  const renderButton = () => {
    if (state.cart.store?.storenumber !== storeDetails.storenumber) {
      return (
        <button
          className="btn__primary"
          onClick={() => setStore(storeDetails)}
        >
          SELECT AS YOUR STORE
        </button>
      )
    }
    return (
      <div className="store-detail__items-select-store">
        <span className="green-checkmark__container">
          <SVG name="check-green" label="check-green" size="sm" />
        </span>
        <label className='select-store__label'>
          SELECTED AS YOUR STORE
        </label>
      </div>
    )
  }

  const formatPhoneNumber = (phoneNumberString) => {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '')
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3]
    }
    return null
  }

  const getHoursDetails = () => {
    return (
      <div className='schedule'>
        <div className='schedule__heading'>
          <p>Hours:</p>
        </div>
        <div className='schedule__table'>
          <div className='schedule__table-days'>
            <p>Monday</p>
            <p>Tuesday</p>
            <p>Wednesday</p>
            <p>Thursday</p>
            <p>Friday</p>
            <p>Saturday</p>
            <p>Sunday</p>
          </div>
          <div className='schedule__table-time'>
            <p>{dayjs(storeDetails.hours_mon.split(':')[0], 'hhmm').format('h:mm A')} - {dayjs(storeDetails.hours_mon.split(':')[1], 'hhmm').format('h:mm A')}</p>
            <p>{dayjs(storeDetails.hours_tue.split(':')[0], 'hhmm').format('h:mm A')} - {dayjs(storeDetails.hours_tue.split(':')[1], 'hhmm').format('h:mm A')}</p>
            <p>{dayjs(storeDetails.hours_wed.split(':')[0], 'hhmm').format('h:mm A')} - {dayjs(storeDetails.hours_wed.split(':')[1], 'hhmm').format('h:mm A')}</p>
            <p>{dayjs(storeDetails.hours_thu.split(':')[0], 'hhmm').format('h:mm A')} - {dayjs(storeDetails.hours_thu.split(':')[1], 'hhmm').format('h:mm A')}</p>
            <p>{dayjs(storeDetails.hours_fri.split(':')[0], 'hhmm').format('h:mm A')} - {dayjs(storeDetails.hours_fri.split(':')[1], 'hhmm').format('h:mm A')}</p>
            <p>{dayjs(storeDetails.hours_sat.split(':')[0], 'hhmm').format('h:mm A')} - {dayjs(storeDetails.hours_sat.split(':')[1], 'hhmm').format('h:mm A')}</p>
            <p>{dayjs(storeDetails.hours_sun.split(':')[0], 'hhmm').format('h:mm A')} - {dayjs(storeDetails.hours_sun.split(':')[1], 'hhmm').format('h:mm A')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="store-detail">
      <div className="store-detail__wrapper">
        <div className="store-detail__items">
          <div className="store-detail__items-name">
            <label>
              {Object.keys(storeDetails).length !== 0 &&
                `${storeDetails.city}, ${storeDetails.state}`}
            </label>
            <span>
              {`${distance && distance.toFixed(2)} miles away from ${Object.keys(state.location).length ? state.location.zip || 'your current location' : state.cart.store?.postalcode}`}
            </span>
          </div>
          <div>
            <Head>
              <title>Visit pOpshelf in {storeDetails.city}, {storeDetails.state}</title>
              <meta name="description" content={storeDetails.address} />
            </Head>
            <div className='maps__store-details'>
              <Image src={mapUrl} alt='Maps' width={0} height={0} sizes="100vw" />
            </div>
          </div>
          <div>
            <div className="store-detail__items-coming-soon">
              {isPreview && <label>coming soon!</label>}
              {isClosed && <label className="error-color">temporarily closed</label>}
            </div>
            <div className="store-detail__items-address">
              <p className="store-detail__items-header"> Address: </p>
              {Object.keys(storeDetails).length !== 0 &&
                `${storeDetails.address}, ${storeDetails.city}, ${storeDetails.state} ${storeDetails.postalcode}`}
              <a href="#" onClick={handleGetDirections}>Directions</a>
            </div>
            {!isPreview && getHoursDetails()}
            {!isPreview &&
              <div className="store-detail__items-phone">
                <p className="store-detail__items-header"> Phone: </p>
                {Object.keys(storeDetails).length !== 0 && formatPhoneNumber(storeDetails.phone)}
              </div>
            }
            {Object.keys(storeDetails).length !== 0 && isSelectable && renderButton()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Store

export const getServerSideProps = async ({ query }) => {
  const res = await fetch(`${publicRuntimeConfig.APP_ENDPOINT}/api/stores/${publicRuntimeConfig.ENV}/store?id=${query.id}&popkey=${publicRuntimeConfig.POP_KEY}`)
  const data = await res.json()
  return {
    props: {
      storeDetails: data[0]
    }
  }
}
